<template>
  <container-slot>
    <v-col cols="12">
      <h1 class="display-1 font-weight-medium">Add New User</h1>
    </v-col>
    <v-col cols="12" v-show="successSubmit">
      <v-alert
        :type="!error ? `success` : `error`"
        ref="alert"
        dismissible
        @input="close"
        class="noMarg"
        v-show="successSubmit"
      >
        {{ msg }}
      </v-alert>
    </v-col>
    <v-col>
      <v-form ref="form" v-model="valid" @submit="submit">
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              prepend-inner-icon="mdi-account"
              name="username"
              label="Username"
              type="text"
              v-model="userName"
              :rules="userNameRules"
              ref="userNameInput"
              outlined
              required
            >
            </v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              prepend-inner-icon="mdi-account-lock"
              name="password"
              label="Password"
              type="password"
              v-model="password"
              :rules="passwordRules"
              ref="passwordInput"
              outlined
              required
            >
            </v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="selectedRole"
              :items="selectRoleData"
              menu-props="auto"
              label="Role"
              prepend-inner-icon="mdi-pencil"
              ref="inputRole"
              :rules="selectRoleRules"
              single-line
              outlined
              required
            ></v-select>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              prepend-inner-icon="mdi-account"
              name="fullname"
              label="Fullname"
              type="text"
              v-model="fullName"
              :rules="fullNameRules"
              ref="fullNameInput"
              outlined
            >
            </v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              prepend-inner-icon="mdi-email"
              name="email"
              label="Email"
              type="email"
              v-model="email"
              :rules="emailRules"
              ref="emailInput"
              outlined
            >
            </v-text-field>
          </v-col>
        </v-row>
        <v-btn
          :disabled="!valid"
          color="primary"
          @click="submit"
          ref="submitBtn"
          :loading="loading"
        >
          Submit
        </v-btn>
      </v-form>
    </v-col>
  </container-slot>
</template>

<script>
import ContainerSlot from "@/components/ContainerSlot";
import { users, roles } from "@/utils/graphqlQuery";
import { insertUser } from "@/utils/graphqlMutation";

export default {
  apollo: {
    selectRoleData: {
      query: roles,
      update: function (data) {
        const { roles } = data;
        const newRoles = roles.map((role) => ({
          text: role.roleName,
          value: role.id,
        }));
        newRoles.push({ text: "Select a Role", value: -1 });
        newRoles.sort((a, b) => a.value - b.value);
        return newRoles;
      },
    },
  },
  data() {
    return {
      error: false,
      msg: "",
      loading: false,
      selectedRole: -1,
      selectRoleData: [{ text: "Select a Role", value: -1 }],
      selectRoleRules: [(v) => v > -1 || "Role must be selected"],
      userName: "",
      userNameRules: [
        (v) => !!v || "Username is required",
        (v) => /^[A-Za-z0-9-_.]{1,}$/.test(v) || "Username must be valid",
      ],
      password: "",
      passwordRules: [
        (v) => !!v || "Password is required",
        (v) =>
          !v || v.length > 8 || "Password must be greater than 8 characters",
      ],
      fullName: "",
      fullNameRules: [
        (v) => !v || /^[A-Za-z ]{1,}$/.test(v) || "Fullname must be valid",
      ],
      email: "",
      emailRules: [
        (v) =>
          !v ||
          /^[\w.-]+@(?:[A-Za-z-]+\.)+[A-Za-z]{2,}$/.test(v) ||
          "Email must be valid",
      ],
      successSubmit: false,
      valid: false,
    };
  },
  components: {
    ContainerSlot,
  },
  methods: {
    async submit(e) {
      e.preventDefault();
      this.successSubmit = false;
      const gqlUsers = users;
      if (this.valid) {
        this.loading = true;
        try {
          const userInput = {
            userName: this.userName,
            password: this.password,
            isActive: 1,
            roleId: this.selectedRole,
            fullName: this.fullName || null,
            email: this.email || null,
          };
          const { data } = await this.$apollo.mutate({
            mutation: insertUser,
            variables: {
              userObj: userInput,
            },
            update: (store, { data: { insertUser: userData } }) => {
              try {
                const cachedUsers = store.readQuery({ query: gqlUsers });
                cachedUsers.users.push(userData.data);
                store.writeQuery({
                  query: gqlUsers,
                  data: {
                    users: cachedUsers,
                  },
                });
                return userData.data;
              } catch (err) {
                // eslint-disable-next-line no-console
                console.log('No "users" cache found or ' + err.message);
                return null;
              }
            },
          });
          const { success, msg } = data.insertUser;
          if (!success) {
            throw new Error(msg);
          }
          this.error = false;
          this.msg = msg;
          this.reset();
        } catch (error) {
          this.error = true;
          this.msg = error.message;
        } finally {
          this.successSubmit = true;
          this.loading = false;
        }
      }
    },
    reset() {
      this.$refs.form.reset();
    },
    close() {
      this.successSubmit = false;
      this.error = false;
      this.loading = false;
      this.msg = "";
    },
  },
  name: "AddUser",
  title() {
    return "Add New User";
  },
};
</script>

<style scoped>
.noMarg {
  margin: 0;
}
</style>
